import React, { useState } from 'react';
import { useConflitos } from '../hooks/useConflitos';
import { MARK } from '../constants';

/**
 * Tela de resolução de conflito.
 *
 * Mostra só os campos em que os dois lados discordam. Listar o registro
 * inteiro faria a pessoa caçar a diferença no meio de dez campos iguais.
 *
 * Nada é decidido sozinho: enquanto ninguém escolhe, o lançamento fica retido
 * e o do servidor permanece como está.
 */

const ROTULOS = {
  value_per_pregnancy: 'Valor por prenhez',
  value_per_te: 'Valor por TE',
  te_quantity: 'Quantidade de TE',
  billed: 'Faturado',
  billed_value: 'Valor faturado',
  paid: 'Pago',
  observations: 'Observações',
  name: 'Fazenda',
  contact: 'Contato',
  phone: 'Telefone',
  service_label: 'Lote',
};

const rotular = (campo) => ROTULOS[campo] || campo.replace(/_/g, ' ');

const mostrar = (v) => {
  if (v === null || v === undefined || v === '') return '—';
  if (typeof v === 'boolean') return v ? 'sim' : 'não';
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
};

const CartaoDeConflito = ({ registro, campos, resolvendo, onResolver }) => {
  // Começa com o lado do aparelho escolhido: é o lançamento que a pessoa fez e
  // que está aqui esperando. Assumir o contrário descartaria por inércia.
  const [escolhas, setEscolhas] = useState(
    () => Object.fromEntries(campos.map((c) => [c.campo, 'meu'])),
  );

  const misturar = () => onResolver(registro, {
    tipo: 'campos',
    campos: Object.fromEntries(
      campos.map((c) => [c.campo, escolhas[c.campo] === 'meu' ? c.meu : c.deles]),
    ),
  });

  return (
    <div className="border border-slate-200 rounded-lg bg-white mb-4">
      <div className="px-4 py-2.5 border-b border-slate-200">
        <p className="text-sm font-semibold text-slate-900">
          {registro.label || registro.op}
        </p>
        <p className="text-xs text-slate-500">
          Alterado por outra pessoa enquanto este lançamento esperava para subir.
        </p>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-slate-50 border-b border-slate-200 text-xs text-slate-600">
              <th className="text-left px-4 py-2 font-medium">Campo</th>
              <th className="text-left px-4 py-2 font-medium">O que você lançou</th>
              <th className="text-left px-4 py-2 font-medium">O que está no servidor</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {campos.map((c) => (
              <tr key={c.campo}>
                <td className="px-4 py-2 text-slate-700">{rotular(c.campo)}</td>
                {['meu', 'deles'].map((lado) => (
                  <td key={lado} className="px-4 py-2">
                    <label className="flex items-start gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name={`${registro.seq}-${c.campo}`}
                        checked={escolhas[c.campo] === lado}
                        onChange={() => setEscolhas((e) => ({ ...e, [c.campo]: lado }))}
                        className="mt-0.5 form-radio h-3.5 w-3.5 text-blue-600"
                      />
                      <span className={escolhas[c.campo] === lado
                        ? 'text-slate-900 font-medium tabular-nums'
                        : 'text-slate-500 tabular-nums'}>
                        {mostrar(lado === 'meu' ? c.meu : c.deles)}
                      </span>
                    </label>
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="px-4 py-3 border-t border-slate-200 flex flex-wrap gap-2">
        <button
          onClick={misturar}
          disabled={resolvendo}
          className="bg-blue-600 text-white px-3 py-1.5 rounded-md text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
        >
          Gravar como está marcado
        </button>
        <button
          onClick={() => onResolver(registro, { tipo: 'meu' })}
          disabled={resolvendo}
          className="bg-white text-slate-700 border border-slate-300 px-3 py-1.5 rounded-md text-sm hover:bg-slate-50 disabled:opacity-50"
        >
          Tudo o que eu lancei
        </button>
        <button
          onClick={() => onResolver(registro, { tipo: 'servidor' })}
          disabled={resolvendo}
          className="bg-white text-slate-700 border border-slate-300 px-3 py-1.5 rounded-md text-sm hover:bg-slate-50 disabled:opacity-50"
        >
          Descartar o meu
        </button>
      </div>
    </div>
  );
};

const Conflitos = () => {
  const { conflitos, resolvendo, resolver, camposEmConflito } = useConflitos();

  if (conflitos.length === 0) return null;

  return (
    <div className="max-w-4xl mx-auto px-4 py-6">
      <div className="mb-4">
        <h2 className="text-base font-semibold text-slate-900">
          <span aria-hidden="true">{MARK.WARN}</span>{' '}
          {conflitos.length === 1
            ? '1 lançamento precisa da sua decisão'
            : `${conflitos.length} lançamentos precisam da sua decisão`}
        </h2>
        <p className="text-sm text-slate-600 mt-1">
          Outra pessoa alterou estes registros enquanto você estava sem conexão.
          Nada foi gravado nem descartado — escolha qual versão vale.
        </p>
      </div>

      {conflitos.map((registro) => (
        <CartaoDeConflito
          key={registro.seq}
          registro={registro}
          campos={camposEmConflito(registro)}
          resolvendo={resolvendo}
          onResolver={resolver}
        />
      ))}
    </div>
  );
};

export default Conflitos;
